import {
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysAi } from '../queryKeys';

import { fetchFilteredModels } from './fetchFilteredModels';
import {
  type FilteredModelsQueryParameters,
  type FilteredModelsResponse,
} from './types';
import { zodFilteredModelsQueryParametersSchema } from './validations';

type FilteredModelsQueryKey = ReturnType<typeof queryKeysAi.getFilteredModels>;

export const getFilteredModelsQueryOptions = <
  TSelected = FilteredModelsResponse,
>({
  queryParameters,
  options,
}: {
  options?: Omit<
    UseQueryOptions<
      FilteredModelsResponse,
      unknown,
      TSelected,
      FilteredModelsQueryKey
    >,
    'queryKey' | 'queryFn'
  >;
  queryParameters: FilteredModelsQueryParameters;
}) => {
  const validatedQueryParameters =
    zodFilteredModelsQueryParametersSchema.parse(queryParameters);

  return queryOptions<
    FilteredModelsResponse,
    unknown,
    TSelected,
    FilteredModelsQueryKey
  >({
    queryFn: async ({ signal }) =>
      fetchFilteredModels(validatedQueryParameters, { signal }),
    queryKey: queryKeysAi.getFilteredModels(validatedQueryParameters),
    staleTime: ms('5m'),
    ...options,
  });
};

/**
 * Custom useQuery hook to fetch all AI models filtered by type.
 * Cached on the server for 30 minutes with background refresh.
 * @param queryParameters - The query parameters to fetch the AI models.
 * @param options - Query options.
 * @returns The filtered AI models.
 * @see https://ai.vestigas.com/redoc#operation/get_filtered_models_get_models_get
 */
export const useQueryFilteredModels = (
  queryParameters: FilteredModelsQueryParameters,
  options?: Omit<
    UseQueryOptions<FilteredModelsResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getFilteredModelsQueryOptions({
      queryParameters,
      options,
    }),
  });
